export const CARE_TYPE_OUTPATIENT = 'outpatient';
export const CARE_TYPE_INPATIENT = 'inpatient';

export const BILLING_PER_VISIT = 'per_visit';
export const BILLING_COURSE_DEFERRED = 'course_deferred';
export const BILLING_COURSE_INSTALLMENTS = 'course_installments';
export const BILLING_COURSE_SPLIT = 'course_split';
export const BILLING_INPATIENT_DAILY = 'inpatient_daily';

export const CARE_TYPE_OPTIONS = [
  { value: CARE_TYPE_OUTPATIENT, label: 'Амбулаторное' },
  { value: CARE_TYPE_INPATIENT, label: 'Стационарное' },
];

export const OUTPATIENT_BILLING_OPTIONS = [
  { value: BILLING_PER_VISIT, label: 'Оплата за каждый визит' },
  { value: BILLING_COURSE_DEFERRED, label: 'Курс: вся сумма на одном визите' },
  { value: BILLING_COURSE_INSTALLMENTS, label: 'Курс: несколько платежей по графику' },
  { value: BILLING_COURSE_SPLIT, label: 'Курс: равными частями на каждом визите' },
];

export const DEFAULT_PAYMENT_SCHEDULE = [
  { visit: 3, amount: 2000 },
  { visit: 6, amount: 2000 },
  { visit: 9, amount: 2000 },
];

export function careTypeLabel(careType) {
  return CARE_TYPE_OPTIONS.find((o) => o.value === careType)?.label ?? 'Амбулаторное';
}

/** Печать справки об оплате — стационар / суточная оплата. */
export function shouldPrintInpatientReceipt(careType, billingMode) {
  const care = careType ?? '';
  const mode = billingMode ?? '';
  return care === CARE_TYPE_INPATIENT || mode === BILLING_INPATIENT_DAILY;
}

/** Нужна ли справка об оплате с суммой (операции, ручной ввод суммы и т.п.). */
export function shouldPrintPaymentReceipt(careType, billingMode, { forcePrint = false } = {}) {
  if (forcePrint) return true;
  return shouldPrintInpatientReceipt(careType, billingMode);
}

export function isInpatientBilling(careType, billingMode) {
  const care = careType ?? '';
  const mode = billingMode ?? '';
  return care === CARE_TYPE_INPATIENT || mode === BILLING_INPATIENT_DAILY;
}

export function bookingBillableAmount(booking, service) {
  const billable = booking?.billableAmount ?? booking?.billable_amount;
  if (billable != null && Number.isFinite(Number(billable))) {
    return Number(billable);
  }
  return Number(service?.price || 0);
}

/** Сколько уже оплачено по записи (нетто, с учётом возвратов). */
export function bookingNetPaidAmount(source) {
  if (!source) return 0;
  const paid = source.paidAmount ?? source.paid_amount;
  if (paid != null && Number.isFinite(Number(paid))) {
    return Number(paid);
  }
  const payments = source.payments;
  if (Array.isArray(payments)) {
    return payments.reduce((sum, payment) => {
      const method = payment.paymentMethod ?? payment.payment_method;
      if (method === 'debt') return sum;
      const amount = Number(payment.amount || 0);
      const refunded = Number(payment.refundedAmount ?? payment.refunded_amount ?? 0);
      return sum + Math.max(0, amount - refunded);
    }, 0);
  }
  return 0;
}

/** У записи уже есть принятая оплата (не долг). */
export function bookingHasSettledPayment(source) {
  if (!source) return false;
  const billable = Number(source.billableAmount ?? source.billable_amount ?? 0);
  const paid = bookingNetPaidAmount(source);
  if (billable > 0 && paid > 0) {
    return paid >= billable - 0.009;
  }
  if (source.needsPayment === true) return false;
  if (source.needsPayment === false) {
    const billable = Number(source.billableAmount ?? source.billable_amount ?? 0);
    const isPay = source.isPaymentVisit ?? source.is_payment_visit;
    if (!isPay && billable <= 0) {
      return false;
    }
    return true;
  }
  if (source.totalRefundable != null) {
    return Number(source.totalRefundable) > 0;
  }
  const payments = source.payments;
  if (Array.isArray(payments)) {
    return payments.some((payment) => {
      const method = payment.paymentMethod ?? payment.payment_method;
      if (method === 'debt') return false;
      const amount = Number(payment.amount || 0);
      const refunded = Number(payment.refundedAmount ?? payment.refunded_amount ?? 0);
      return amount - refunded > 0.009;
    });
  }
  return false;
}

/** Можно принять оплату по уже существующей записи (не сразу после оформления стационара). */
export function bookingPaymentDue(booking, service) {
  if (!booking?.id) return false;
  if (bookingHasSettledPayment(booking)) return false;
  const billable = bookingBillableAmount(booking, service);
  const isPay = booking?.isPaymentVisit ?? booking?.is_payment_visit;
  return !!isPay && billable > 0;
}

/** Сразу после создания записи — оплату предлагать только амбулаторным визитам с суммой к оплате. */
export function shouldOfferPaymentAfterBookingCreate(booking, service) {
  if (isInpatientBilling(
    booking?.careType ?? booking?.care_type ?? service?.careType ?? service?.care_type,
    booking?.billingMode ?? booking?.billing_mode ?? service?.billingMode ?? service?.billing_mode
  )) {
    return false;
  }
  return bookingPaymentDue(booking, service);
}

export function billingModeLabel(mode) {
  return OUTPATIENT_BILLING_OPTIONS.find((o) => o.value === mode)?.label ?? mode;
}

function scheduleList(service) {
  const raw = service.paymentSchedule ?? service.payment_schedule;
  return Array.isArray(raw) ? raw : [];
}

export function billingSummary(service) {
  if (!service) return '';
  const care = service.careType ?? service.care_type ?? CARE_TYPE_OUTPATIENT;
  const mode = service.billingMode ?? service.billing_mode ?? BILLING_PER_VISIT;
  const visits = service.courseVisitsCount ?? service.course_visits_count;

  if (care === CARE_TYPE_INPATIENT || mode === BILLING_INPATIENT_DAILY) {
    const daily = Number(service.price || 0).toLocaleString();
    const days = visits ? `, план ${visits} дн.` : '';
    return `Стационар · ${daily} сом/сутки${days}`;
  }

  if (mode === BILLING_COURSE_DEFERRED) {
    const n = service.paymentDueOnVisit ?? service.payment_due_on_visit ?? 1;
    const total = service.coursePrice ?? service.course_price ?? 0;
    const visitsPart = visits ? ` (${visits} виз.)` : '';
    return `Курс${visitsPart}: ${Number(total).toLocaleString()} сом на ${n}-м визите`;
  }

  if (mode === BILLING_COURSE_INSTALLMENTS) {
    const rows = scheduleList(service);
    const parts = rows.map((r) => `${r.visit}-й: ${Number(r.amount).toLocaleString()}`).join(', ');
    return parts ? `Курс: платежи ${parts} сом` : 'Курс: график платежей';
  }

  if (mode === BILLING_COURSE_SPLIT) {
    const total = service.coursePrice ?? service.course_price ?? 0;
    const n = visits || 1;
    const per = Math.round((Number(total) / n) * 100) / 100;
    return `Курс ${n} виз.: ~${per.toLocaleString()} сом за визит (всего ${Number(total).toLocaleString()})`;
  }

  return `Амбулаторно · ${Number(service.price || 0).toLocaleString()} сом/визит`;
}

export function bookingPaymentHint(booking, service) {
  if (!service && !booking) return null;

  const visitNum = booking?.visitNumber ?? booking?.visit_number;
  const dayOfStay = booking?.dayOfStay ?? booking?.day_of_stay;
  const billable = booking?.billableAmount ?? booking?.billable_amount;
  const isPay = booking?.isPaymentVisit ?? booking?.is_payment_visit;
  const mode = service?.billingMode ?? service?.billing_mode;
  const care = service?.careType ?? service?.care_type;

  if (care === CARE_TYPE_INPATIENT && dayOfStay != null) {
    const daily = Number(billable ?? service?.price ?? 0).toLocaleString();
    return `Стационар · сутки ${dayOfStay} · ${daily} сом/сутки. Оплата по мере пребывания — вкладка «Стационар» у регистратора.`;
  }

  if (visitNum != null && billable != null) {
    if (isPay && Number(billable) > 0) {
      return `Визит №${visitNum} — к оплате ${Number(billable).toLocaleString()} сом`;
    }
    if (Number(billable) === 0) {
      if (mode === BILLING_COURSE_DEFERRED) {
        const due = service?.paymentDueOnVisit ?? service?.payment_due_on_visit;
        return `Визит №${visitNum} — без оплаты (полная оплата на ${due}-м визите)`;
      }
      if (mode === BILLING_COURSE_INSTALLMENTS) {
        const next = scheduleList(service).find((r) => r.visit > visitNum);
        return next
          ? `Визит №${visitNum} — без оплаты (след. платёж на ${next.visit}-м, ${Number(next.amount).toLocaleString()} сом)`
          : `Визит №${visitNum} — без оплаты по графику`;
      }
      return `Визит №${visitNum} — без оплаты`;
    }
  }

  return billingSummary(service);
}

export function sumSchedule(schedule) {
  return (schedule || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
}
